import { Routes } from '@angular/router';
import { AppLayoutComponent } from '@shared/layout/app-layout/app-layout.component';
import { authGuard } from '@shared/auth/guards/auth.guard';
import { redirectAuthenticatedGuard } from '@shared/auth/guards/redirect-authenticated.guard';
import { adminGuard } from '@shared/auth/guards/admin.guard';
import { permissionGuard, requireActionGuard } from '@shared/auth/guards/permission.guard';
import { transferenciaTokenGuard } from '@partnercore/feature-settings/configuracion/transferencia-confirmar/transferencia-token.guard';
import { transferenciaConfirmarResolver } from '@partnercore/feature-settings/configuracion/transferencia-confirmar/transferencia-confirmar.resolver';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'auth/login',
    pathMatch: 'full',
  },
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: 'inicio',
        loadComponent: () => import('@partnercore/feature-home/inicio/inicio.component').then(m => m.InicioComponent),
        title: 'titles.inicio',
      },
      {
        path: 'notifications',
        loadComponent: () => import('@partnercore/feature-modules/modulos/control-general/notificaciones/notifications/notifications.component').then(m => m.NotificationsComponent),
        title: 'titles.notificaciones',
      },
      {
        path: 'configuracion',
        loadComponent: () => import('@partnercore/feature-settings/configuracion/configuracion.component').then(m => m.ConfiguracionComponent),
        title: 'titles.configuracion',
      },
      {
        path: 'soporte',
        loadComponent: () => import('@partnercore/feature-settings/soporte/soporte.component').then(m => m.SoporteComponent),
        title: 'titles.soporte',
      },
      {
        path: 'configuracion/transferir',
        loadComponent: () => import('@partnercore/feature-settings/configuracion/transferencia-confirmar/transferencia-confirmar.component').then(m => m.TransferenciaConfirmarComponent),
        title: 'titles.confirmTransfer',
        canActivate: [transferenciaTokenGuard],
        resolve: { transferenciaInfo: transferenciaConfirmarResolver },
      },
      // Dashboard Sistema
      {
        path: 'dashboard-system',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'dashboard-general',
            pathMatch: 'full',
          },
          {
            path: 'dashboard-general',
            loadComponent: () => import('@partnercore/feature-dashboard/dashboard/general/dashboard-general.component').then(m => m.DashboardGeneralComponent),
            canActivate: [permissionGuard],
            title: 'titles.dashboardGeneral',
          },
        ],
      },
      // Módulos Admin Sistema
      {
        path: 'admin-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'usuarios',
            pathMatch: 'full',
          },
          {
            path: 'usuarios',
            loadComponent: () => import('@partnercore/feature-admin/users/users.component').then(m => m.UsersComponent),
            canActivate: [permissionGuard],
            title: 'titles.usuariosSistema',
          },
          {
            path: 'permisos',
            loadComponent: () => import('@partnercore/feature-admin/permisos').then(m => m.PermisosComponent),
            canActivate: [permissionGuard],
            title: 'titles.permisosSistema',
          },
          {
            path: 'permisos/editar/rol/:id',
            loadComponent: () => import('@partnercore/feature-admin/permisos-editar').then(m => m.PermisosEditarComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.permisosSistema',
          },
          {
            path: 'roles',
            loadComponent: () => import('@partnercore/feature-admin/roles/roles.component').then(m => m.RolesComponent),
            canActivate: [permissionGuard],
            title: 'titles.rolesSistema',
          },
          {
            path: 'perfil',
            loadComponent: () => import('@partnercore/feature-profile/perfil/perfil.component').then(m => m.PerfilComponent),
            canActivate: [permissionGuard],
            title: 'titles.perfilSistema',
          },
          {
            path: 'empresas',
            loadComponent: () => import('@partnercore/feature-admin/empresas/empresas.component').then(m => m.EmpresasComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresasSistema',
          },
          {
            path: 'empresas/:id',
            loadComponent: () => import('@partnercore/feature-admin/empresas/components/empresa-detail/empresa-detail.component').then(m => m.EmpresaDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresaDetalle',
          },
          {
            path: 'clientes',
            loadComponent: () => import('@partnercore/feature-admin/clientes/clientes.component').then(m => m.ClientesComponent),
            canActivate: [permissionGuard],
            title: 'titles.clientesSistema',
          },
          {
            path: 'planes',
            loadComponent: () => import('@partnercore/feature-admin/planes/planes.component').then(m => m.PlanesComponent),
            canActivate: [permissionGuard],
            title: 'titles.planesSistema',
          },
          {
            path: 'planes/:id',
            loadComponent: () => import('@partnercore/feature-admin/planes/components/plan-detail/plan-detail.component').then(m => m.PlanDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.planesSistema',
          },
          {
            path: 'productos',
            loadComponent: () => import('@partnercore/feature-admin/productos/productos.component').then(m => m.ProductosComponent),
            canActivate: [permissionGuard],
            title: 'titles.productosSistema',
          },
        ],
      },
      // Seguridad Sistema (SYS) — ruta top-level para que coincida con la URL sembrada en DB: /seguridad-sistema/*
      {
        path: 'seguridad-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'sesiones',
            pathMatch: 'full',
          },
          {
            path: 'sesiones',
            loadComponent: () => import('@partnercore/feature-sessions/sesiones/sesiones.component').then(m => m.SesionesComponent),
            canActivate: [permissionGuard],
            data: { isSys: true },
            title: 'titles.sesionesSistema',
          },
          {
            path: 'auditorias',
            loadComponent: () => import('@partnercore/feature-audit/auditoria/auditoria.component').then(m => m.AuditoriaComponent),
            canActivate: [permissionGuard],
            data: { isSys: true },
            title: 'titles.auditSistema',
          },
        ],
      },
      // Dashboard Control General
      {
        path: 'dashboard',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'general',
            pathMatch: 'full',
          },
          {
            path: 'general',
            loadComponent: () => import('@partnercore/feature-dashboard/dashboard/general/dashboard-general.component').then(m => m.DashboardGeneralComponent),
            canActivate: [permissionGuard],
            title: 'titles.dashboardGeneral',
          },
        ],
      },
      // Módulos Administrativos
      {
        path: 'admin',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'usuarios',
            pathMatch: 'full',
          },
          {
            path: 'usuarios',
            loadComponent: () => import('@partnercore/feature-admin/users/users.component').then(m => m.UsersComponent),
            canActivate: [permissionGuard],
            title: 'titles.usuarios',
          },
          {
            path: 'permisos',
            loadComponent: () => import('@partnercore/feature-admin/permisos').then(m => m.PermisosComponent),
            canActivate: [permissionGuard],
            title: 'titles.permisos',
          },
          {
            path: 'permisos/editar/rol/:id',
            loadComponent: () => import('@partnercore/feature-admin/permisos-editar').then(m => m.PermisosEditarComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.permisos',
          },
          {
            path: 'roles',
            loadComponent: () => import('@partnercore/feature-admin/roles/roles.component').then(m => m.RolesComponent),
            canActivate: [permissionGuard],
            title: 'titles.rolesAdmin',
          },
          {
            path: 'perfil',
            loadComponent: () => import('@partnercore/feature-profile/perfil/perfil.component').then(m => m.PerfilComponent),
            canActivate: [permissionGuard],
            title: 'titles.perfil',
          },
          {
            path: 'empresas',
            loadComponent: () => import('@partnercore/feature-admin/empresas/empresas.component').then(m => m.EmpresasComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresas',
          },
          {
            path: 'empresas/:id',
            loadComponent: () => import('@partnercore/feature-admin/empresas/components/empresa-detail/empresa-detail.component').then(m => m.EmpresaDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresaDetalle',
          },
        ],
      },
      // Estructura
      {
        path: 'estructura-system',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'modulos-sistema',
            pathMatch: 'full',
          },
          {
            path: 'modulos-sistema',
            loadComponent: () => import('@partnercore/feature-modules/modulos/module.component').then(m => m.ModuleComponent),
            canActivate: [permissionGuard],
            title: 'titles.modulosSistema',
          },
        ],
      },
      // Maestros Sistema
      {
        path: 'maestros-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'monedas',
            pathMatch: 'full',
          },
          {
            path: 'monedas',
            loadComponent: () => import('@partnercore/feature-currencies/currencies/currencies.component').then(m => m.CurrenciesComponent),
            canActivate: [permissionGuard],
            title: 'titles.monedasSistema',
          },
        ],
      },
      // Maestros
      {
        path: 'maestros',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'monedas',
            pathMatch: 'full',
          },
          {
            path: 'monedas',
            loadComponent: () => import('@partnercore/feature-currencies/currencies/currencies.component').then(m => m.CurrenciesComponent),
            canActivate: [permissionGuard],
            title: 'titles.monedas',
          },
        ],
      },
      // Maestros de Producción (MES) — URLs sembradas en DB: /maestros-produccion/*
      {
        path: 'maestros-produccion',
        canActivate: [permissionGuard],
        children: [
          {
            path: 'tablas-apoyo',
            loadComponent: () => import('@praxion/feature-master-data/tablas-apoyo/tablas-apoyo.component').then(m => m.TablasApoyoComponent),
            canActivate: [permissionGuard],
            title: 'titles.tablasApoyo',
          },
          {
            path: 'materias-primas',
            loadComponent: () => import('@praxion/feature-master-data/materias-primas/materias-primas.component').then(m => m.MateriasPrimasComponent),
            canActivate: [permissionGuard],
            title: 'titles.materiasPrimas',
          },
          {
            path: 'materias-primas/:id',
            loadComponent: () => import('@praxion/feature-master-data/materias-primas/components/rm-detail/rm-detail.component').then(m => m.RmDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.materiaPrimaDetalle',
          },
          {
            path: 'plantas',
            loadComponent: () => import('@praxion/feature-master-data/plantas/plantas.component').then(m => m.PlantasComponent),
            canActivate: [permissionGuard],
            title: 'titles.plantas',
          },
          {
            path: 'plantas/:id',
            loadComponent: () => import('@praxion/feature-master-data/plantas/components/pl-detail/pl-detail.component').then(m => m.PlDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.plantaDetalle',
          },
          {
            path: 'centros-costo',
            loadComponent: () => import('@praxion/feature-master-data/centros-costo/centros-costo.component').then(m => m.CentrosCostoComponent),
            canActivate: [permissionGuard],
            title: 'titles.centrosCosto',
          },
          {
            path: 'centros-costo/:id',
            loadComponent: () => import('@praxion/feature-master-data/centros-costo/components/cc-detail/cc-detail.component').then(m => m.CcDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.centroCostoDetalle',
          },
          {
            path: 'turnos',
            loadComponent: () => import('@praxion/feature-master-data/turnos/turnos.component').then(m => m.TurnosComponent),
            canActivate: [permissionGuard],
            title: 'titles.turnosProduccion',
          },
          {
            path: 'lineas-produccion',
            loadComponent: () => import('@praxion/feature-master-data/lineas-produccion/lineas-produccion.component').then(m => m.LineasProduccionComponent),
            canActivate: [permissionGuard],
            title: 'titles.lineasProduccion',
          },
          {
            path: 'lineas-produccion/:id',
            loadComponent: () => import('@praxion/feature-master-data/lineas-produccion/components/ln-detail/ln-detail.component').then(m => m.LnDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.lineaProduccionDetalle',
          },
          {
            path: 'productos',
            loadComponent: () => import('@praxion/feature-master-data/productos/productos.component').then(m => m.ProductosComponent),
            canActivate: [permissionGuard],
            title: 'titles.productosTerminados',
          },
          {
            path: 'productos/:id',
            loadComponent: () => import('@praxion/feature-master-data/productos/components/product-detail/product-detail.component').then(m => m.ProductDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.productoDetalle',
          },
          {
            path: 'formulas-recetas',
            loadComponent: () => import('@praxion/feature-master-data/formulas/formulas.component').then(m => m.FormulasComponent),
            canActivate: [permissionGuard],
            title: 'titles.formulasRecetas',
          },
          {
            path: 'formulas-recetas/:id',
            loadComponent: () => import('@praxion/feature-master-data/formulas/components/formula-detail/formula-detail.component').then(m => m.FormulaDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.formulaDetalle',
          },
          {
            path: 'formulas',
            redirectTo: 'formulas-recetas',
            pathMatch: 'full'
          },
          {
            path: 'formulas/:id',
            redirectTo: 'formulas-recetas/:id',
            pathMatch: 'full'
          },
          {
            path: 'periodos',
            loadComponent: () => import('@praxion/feature-master-data/periodos/periodos.component').then(m => m.PeriodosComponent),
            canActivate: [permissionGuard],
            title: 'titles.periodos',
          },
          {
            path: 'modelos-evaluacion',
            loadComponent: () => import('@praxion/feature-master-data/modelos-evaluacion/modelos-evaluacion.component').then(m => m.ModelosEvaluacionComponent),
            canActivate: [permissionGuard],
            title: 'titles.modelosEvaluacion',
          },
          {
            path: 'aprobadores',
            loadComponent: () => import('@praxion/feature-master-data/aprobadores/aprobadores.component').then(m => m.AprobadoresComponent),
            canActivate: [permissionGuard],
            title: 'titles.aprobadores',
          },
        ],
      },
      // Planificación de Producción (MES) — URLs sembradas en DB: /planificacion/*
      {
        path: 'planificacion',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'ordenes',
            pathMatch: 'full',
          },
          {
            path: 'creacion-ops',
            loadComponent: () => import('@praxion/feature-planificacion/ordenes').then(m => m.ProductionOrderComponent),
            canActivate: [permissionGuard],
            title: 'titles.ordenesProduccion',
          },
          {
            path: 'ordenes',
            redirectTo: 'creacion-ops',
            pathMatch: 'full',
          },
          {
            path: 'verificacion-materiales',
            loadComponent: () => import('@praxion/feature-planificacion/verificacion-materiales').then(m => m.MaterialVerificationComponent),
            canActivate: [permissionGuard],
            title: 'titles.verificacionMateriales',
          },
          {
            path: 'programacion',
            loadComponent: () => import('@praxion/feature-planificacion/programacion').then(m => m.ProductionScheduleComponent),
            canActivate: [permissionGuard],
            title: 'titles.programacionProduccion',
          },
          {
            path: 'liberacion',
            loadComponent: () => import('@praxion/feature-planificacion/liberacion').then(m => m.ProductionReleaseComponent),
            canActivate: [permissionGuard],
            title: 'titles.liberacionAprobacion',
          },
          {
            path: 'reprogramacion',
            loadComponent: () => import('@praxion/feature-planificacion/reprogramacion').then(m => m.ProductionAdjustmentComponent),
            canActivate: [permissionGuard],
            title: 'titles.ajusteReprogramacion',
          },
        ],
      },
      // Ejecución de Producción (MES) — URLs sembradas en DB: /ejecucion/*
      {
        path: 'ejecucion',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'panel-piso',
            pathMatch: 'full',
          },
          {
            path: 'panel-piso',
            loadComponent: () => import('@praxion/feature-ejecucion/panel-piso').then(m => m.FloorPanelComponent),
            canActivate: [permissionGuard],
            title: 'titles.panelPiso',
          },
          {
            path: 'registro-avance',
            loadComponent: () => import('@praxion/feature-ejecucion/registro-avance').then(m => m.RegistroAvanceComponent),
            canActivate: [permissionGuard],
            title: 'titles.registroAvance',
          },
          {
            // El analisis de UNA OP tiene su propia direccion: asi «Analizar» puede abrirse en otra
            // pestana, se puede enviar por chat y el navegador recuerda donde estabas. Es el mismo
            // componente: la ruta sin `:opId` lista, y con `:opId` abre esa OP ya analizada.
            path: 'registro-avance/:opId',
            loadComponent: () => import('@praxion/feature-ejecucion/registro-avance').then(m => m.RegistroAvanceComponent),
            canActivate: [permissionGuard],
            title: 'titles.registroAvance',
          },
          {
            path: 'paradas',
            loadComponent: () => import('@praxion/feature-ejecucion/paradas').then(m => m.ParadasComponent),
            canActivate: [permissionGuard],
            title: 'titles.paradas',
          },
          {
            path: 'mermas',
            loadComponent: () => import('@praxion/feature-ejecucion/mermas').then(m => m.MermasComponent),
            canActivate: [permissionGuard],
            title: 'titles.mermas',
          },
          {
            path: 'consumos',
            loadComponent: () => import('@praxion/feature-ejecucion/consumos').then(m => m.ConsumosComponent),
            canActivate: [permissionGuard],
            title: 'titles.consumos',
          },
          {
            path: 'turnos',
            loadComponent: () => import('@praxion/feature-ejecucion/turnos').then(m => m.TurnosComponent),
            canActivate: [permissionGuard],
            title: 'titles.turnos',
          },
        ],
      },
      // Trazabilidad y Gestión de Lotes (MES) — URLs sembradas en DB: /trazabilidad/*
      {
        path: 'trazabilidad',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'consulta-alertas',
            pathMatch: 'full',
          },
          {
            path: 'consulta-alertas',
            loadComponent: () => import('@praxion/feature-trazabilidad/consulta-alertas').then(m => m.ConsultaAlertasComponent),
            canActivate: [permissionGuard],
            title: 'titles.consultaAlertas',
          },
          {
            path: 'genealogia',
            loadComponent: () => import('@praxion/feature-trazabilidad/genealogia').then(m => m.GenealogiaComponent),
            canActivate: [permissionGuard],
            title: 'titles.genealogia',
          },
          {
            path: 'por-orden',
            loadComponent: () => import('@praxion/feature-trazabilidad/por-orden').then(m => m.PorOrdenComponent),
            canActivate: [permissionGuard],
            title: 'titles.trazabilidadPorOrden',
          },
          {
            path: 'lotes-materia-prima',
            loadComponent: () => import('@praxion/feature-trazabilidad/lotes-materia-prima').then(m => m.LotesMateriaPrimaComponent),
            canActivate: [permissionGuard],
            title: 'titles.lotesMateriaPrima',
          },
        ],
      },
      // Control de Calidad en Producción (MES) — URLs sembradas en DB: /calidad/*
      {
        path: 'calidad',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'planes-control',
            pathMatch: 'full',
          },
          {
            path: 'planes-control',
            loadComponent: () => import('@praxion/feature-calidad/planes-control').then(m => m.PlanesControlComponent),
            canActivate: [permissionGuard],
            title: 'titles.planesControl',
          },
          {
            path: 'inspecciones-proceso',
            loadComponent: () => import('@praxion/feature-calidad/inspecciones-proceso').then(m => m.InspeccionesProcesoComponent),
            canActivate: [permissionGuard],
            title: 'titles.inspeccionesProceso',
          },
          {
            path: 'inspeccion-producto-terminado',
            loadComponent: () => import('@praxion/feature-calidad/inspeccion-producto-terminado').then(m => m.InspeccionProductoTerminadoComponent),
            canActivate: [permissionGuard],
            title: 'titles.inspeccionProductoTerminado',
          },
          {
            path: 'no-conformidades',
            loadComponent: () => import('@praxion/feature-calidad/no-conformidades').then(m => m.NoConformidadesComponent),
            canActivate: [permissionGuard],
            title: 'titles.noConformidades',
          },
          {
            path: 'historial-estadisticas',
            loadComponent: () => import('@praxion/feature-calidad/historial-estadisticas').then(m => m.HistorialEstadisticasComponent),
            canActivate: [permissionGuard],
            title: 'titles.historialEstadisticas',
          },
        ],
      },
      // Indicadores (MES) — URLs sembradas en DB: /indicadores/*
      {
        path: 'indicadores',
        children: [
          {
            path: 'dashboard-ejecutivo',
            loadComponent: () => import('@praxion/feature-indicadores/dashboard-ejecutivo').then(m => m.DashboardEjecutivoComponent),
            canActivate: [permissionGuard],
            title: 'titles.indicadoresDashboard',
          },
          {
            path: 'reportes-gerenciales',
            loadComponent: () => import('@praxion/feature-indicadores/reportes-gerenciales').then(m => m.ReportesGerencialesComponent),
            canActivate: [permissionGuard],
            title: 'titles.indicadoresReportes',
          },
          {
            path: 'produccion',
            loadComponent: () => import('@praxion/feature-indicadores/produccion').then(m => m.ProduccionComponent),
            canActivate: [permissionGuard],
            title: 'titles.indicadoresProduccion',
          },
          {
            path: 'oee',
            loadComponent: () => import('@praxion/feature-indicadores/oee').then(m => m.OeeComponent),
            canActivate: [permissionGuard],
            title: 'titles.indicadoresOee',
          },
          {
            path: 'analisis-paradas',
            loadComponent: () => import('@praxion/feature-indicadores/analisis-paradas').then(m => m.AnalisisParadasComponent),
            canActivate: [permissionGuard],
            title: 'titles.indicadoresParadas',
          },
        ],
      },
      // Costos de Producción (MES) — URLs sembradas en DB: /costos/*
      //
      // Los CINCO módulos están sembrados en `DataInitializer` desde antes, y hasta hoy los cinco
      // llevaban a la página de 404: `verificar_catalogo_rutas.sh` los contaba como 5 de sus 6
      // desalineaciones. Con esta ruta quedan 4, y Pedro retira esos cuatro del catálogo al desplegar
      // hasta que tengan pantalla.
      {
        path: 'costos',
        children: [
          {
            path: 'costos-estandar',
            loadComponent: () => import('@praxion/feature-costos/costos-estandar').then(m => m.CostosEstandarComponent),
            canActivate: [permissionGuard],
            title: 'titles.costosEstandar',
          },
          {
            // `RF-CCR-09`. La URL es la del catálogo —`DataInitializer`: «Costos Reales por OP»—,
            // y con esta ruta deja de ser una de las cuatro de Costos que dan 404.
            path: 'costos-reales-op',
            loadComponent: () => import('@praxion/feature-costos/costos-reales').then(m => m.CostosRealesComponent),
            canActivate: [permissionGuard],
            title: 'titles.costosReales',
          },
          {
            // `RF-AD-01` y `RF-AD-02`. Misma historia: la URL estaba en el catálogo desde el
            // principio y no tenía pantalla, así que era uno de los 404. Con esta ruta quedan 2.
            path: 'analisis-desviaciones',
            loadComponent: () => import('@praxion/feature-costos/analisis-desviaciones').then(m => m.AnalisisDesviacionesComponent),
            canActivate: [permissionGuard],
            title: 'titles.analisisDesviaciones',
          },
          {
            // `RF-CMS-04`. La cuarta de las cinco: con esta ruta sólo queda `reportes` dando 404.
            path: 'costeo-mermas-scrap',
            loadComponent: () => import('@praxion/feature-costos/costeo-mermas').then(m => m.CosteoMermasComponent),
            canActivate: [permissionGuard],
            title: 'titles.costeoMermasScrap',
          },
          {
            // `RF-RC-03` y `RF-RC-06`. La quinta y última: con esta ruta **Costos no tiene ningún
            // módulo del catálogo sin pantalla**.
            path: 'reportes',
            loadComponent: () => import('@praxion/feature-costos/reportes').then(m => m.ReportesCostosComponent),
            canActivate: [permissionGuard],
            title: 'titles.reportesCostos',
          },
        ],
      },
      // Gestión de Compras (MES) — URLs sembradas en DB: /compras/*
      {
        path: 'compras',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'proveedores',
            pathMatch: 'full',
          },
          {
            path: 'proveedores',
            loadComponent: () => import('@praxion/feature-compras/proveedores/proveedores.component').then(m => m.ProveedoresComponent),
            canActivate: [permissionGuard],
            title: 'titles.proveedores',
          },
          {
            path: 'solicitudes',
            loadComponent: () => import('@praxion/feature-compras/solicitudes/solicitudes.component').then(m => m.SolicitudesComponent),
            canActivate: [permissionGuard],
            title: 'titles.solicitudesCompra',
          },
          {
            path: 'cotizaciones',
            loadComponent: () => import('@praxion/feature-compras/cotizaciones/cotizaciones.component').then(m => m.CotizacionesComponent),
            canActivate: [permissionGuard],
            title: 'titles.cotizacionesRfq',
          },
          {
            path: 'ordenes',
            loadComponent: () => import('@praxion/feature-compras/ordenes/ordenes.component').then(m => m.OrdenesComponent),
            canActivate: [permissionGuard],
            title: 'titles.ordenesCompra',
          },
          {
            path: 'recepciones',
            loadComponent: () => import('@praxion/feature-compras/recepciones/recepciones.component').then(m => m.RecepcionesComponent),
            canActivate: [permissionGuard],
            title: 'titles.recepcionCompras',
          },
          {
            path: 'evaluacion-proveedores',
            loadComponent: () => import('@praxion/feature-compras/evaluacion/evaluacion.component').then(m => m.EvaluacionComponent),
            canActivate: [permissionGuard],
            title: 'titles.evaluacionProveedores',
          },
          {
            // §3.6 (12-10): el submódulo se llama Importaciones. La ruta ES la clave de permisos
            // (`MODULE_URL`), así que cambia aquí, en el catálogo (V74) y en los seis sitios del
            // código a la vez: a medias, la API deniega el módulo entero.
            path: 'importaciones',
            loadComponent: () => import('@praxion/feature-compras/import-export/import-export.component').then(m => m.ImportExportComponent),
            canActivate: [permissionGuard],
            title: 'titles.importaciones',
          },
        ],
      },
      // Gestión de Mantenimiento (MES) — URLs sembradas en DB: /mantenimiento/*
      {
        path: 'mantenimiento',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'maquinas',
            pathMatch: 'full',
          },
          {
            path: 'maquinas',
            loadComponent: () => import('@praxion/feature-mantenimiento/maquinas').then(m => m.MaquinasComponent),
            canActivate: [permissionGuard],
            title: 'titles.maquinasEquipos',
          },
          {
            // La ficha de maquina es PAGINA, como `materias-primas/:id` y `productos/:id`. Era un
            // modal y cambiaba de tamano en cada pestana, porque el contenedor se ajustaba al
            // contenido y tres de las cuatro estan casi vacias.
            path: 'maquinas/:id',
            loadComponent: () => import('@praxion/feature-mantenimiento/maquinas/components/equipment-detail/equipment-detail.component').then(m => m.EquipmentDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.fichaMaquina',
          },
          {
            path: 'repuestos',
            loadComponent: () => import('@praxion/feature-mantenimiento/repuestos').then(m => m.RepuestosComponent),
            canActivate: [permissionGuard],
            title: 'titles.repuestosMantenimiento',
          },
          {
            // La ficha del repuesto es una pagina, no un modal (2026-09-20). Mismo patron que
            // `maquinas/:id` y las fichas de Maestros.
            path: 'repuestos/:id',
            loadComponent: () => import('@praxion/feature-mantenimiento/repuestos/components/spare-part-detail/spare-part-detail.component').then(m => m.SparePartDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.fichaRepuesto',
          },
          {
            path: 'preventivo',
            loadComponent: () => import('@praxion/feature-mantenimiento/preventivo').then(m => m.PreventivoComponent),
            canActivate: [permissionGuard],
            title: 'titles.mantenimientoPreventivo',
          },
          {
            path: 'ordenes-trabajo',
            loadComponent: () => import('@praxion/feature-mantenimiento/ordenes-trabajo').then(m => m.OrdenesTrabajoComponent),
            canActivate: [permissionGuard],
            title: 'titles.ordenesTrabajo',
          },
          {
            path: 'averias',
            loadComponent: () => import('@praxion/feature-mantenimiento/averias').then(m => m.AveriasComponent),
            canActivate: [permissionGuard],
            title: 'titles.registroAverias',
          },
          {
            path: 'historial',
            loadComponent: () => import('@praxion/feature-mantenimiento/historial').then(m => m.HistorialComponent),
            canActivate: [permissionGuard],
            title: 'titles.historialMantenimiento',
          },
          {
            path: 'lubricacion',
            loadComponent: () => import('@praxion/feature-mantenimiento/lubricacion').then(m => m.LubricacionComponent),
            canActivate: [permissionGuard],
            title: 'titles.planesLubricacion',
          },
          {
            path: 'registros-operativos',
            loadComponent: () => import('@praxion/feature-mantenimiento/registros-operativos').then(m => m.RegistrosOperativosComponent),
            canActivate: [permissionGuard],
            title: 'titles.registrosOperativos',
          },
        ],
      },

      // Gestión de Inventario (MES) — URLs sembradas en DB: /inventario/*
      {
        path: 'inventario',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'ubicaciones',
            pathMatch: 'full',
          },
          {
            path: 'lotes',
            loadComponent: () => import('@praxion/feature-inventario/lotes').then(m => m.LotesComponent),
            canActivate: [permissionGuard],
            title: 'titles.lotesInventario',
          },
          {
            path: 'ubicaciones',
            loadComponent: () => import('@praxion/feature-inventario/ubicaciones/ubicaciones.component').then(m => m.UbicacionesComponent),
            canActivate: [permissionGuard],
            title: 'titles.ubicacionesAlmacenes',
          },
          {
            path: 'recepciones',
            // Misma pantalla que /compras/recepciones: una recepción es un solo hecho de negocio y
            // se consulta desde las dos entradas del catálogo, cada una con su permiso. El
            // componente resuelve su URL de módulo de la ruta activa (`receptionModuleUrl`) y el
            // backend hace lo propio con la cabecera `X-Module-Context`.
            loadComponent: () => import('@praxion/feature-compras/recepciones/recepciones.component').then(m => m.RecepcionesComponent),
            canActivate: [permissionGuard],
            title: 'titles.recepcionesAlmacen',
          },
          {
            path: 'movimientos',
            loadComponent: () => import('@praxion/feature-inventario/movimientos/movimientos.component').then(m => m.MovimientosComponent),
            canActivate: [permissionGuard],
            title: 'titles.movimientosStock',
          },
          {
            path: 'despachos',
            loadComponent: () => import('@praxion/feature-inventario/despachos/despachos.component').then(m => m.DespachosComponent),
            canActivate: [permissionGuard],
            title: 'titles.despachos',
          },
          {
            path: 'listas-recoleccion',
            loadComponent: () => import('@praxion/feature-inventario/listas-recoleccion/listas-recoleccion.component').then(m => m.ListasRecoleccionComponent),
            canActivate: [permissionGuard],
            title: 'titles.listasRecoleccion',
          },
          {
            path: 'inventario-fisico',
            loadComponent: () => import('@praxion/feature-inventario/inventario-fisico/inventario-fisico.component').then(m => m.InventarioFisicoComponent),
            canActivate: [permissionGuard],
            title: 'titles.inventarioFisico',
          },
          {
            path: 'ajustes',
            loadComponent: () => import('@praxion/feature-inventario/ajustes/ajustes.component').then(m => m.AjustesComponent),
            canActivate: [permissionGuard],
            title: 'titles.ajustesInventario',
          },
        ],
      },
      // Seguridad Control General — ruta top-level para que coincida con la URL sembrada en DB: /seguridad/*
      {
        path: 'seguridad',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'sesiones',
            pathMatch: 'full',
          },
          {
            path: 'sesiones',
            loadComponent: () => import('@partnercore/feature-sessions/sesiones/sesiones.component').then(m => m.SesionesComponent),
            canActivate: [permissionGuard],
            data: { isSys: false },
            title: 'titles.sesiones',
          },
          {
            path: 'auditorias',
            loadComponent: () => import('@partnercore/feature-audit/auditoria/auditoria.component').then(m => m.AuditoriaComponent),
            canActivate: [permissionGuard],
            data: { isSys: false },
            title: 'titles.audit',
          },
        ],
      },
    ],
  },
  // Auth
  {
    path: 'auth',
    loadComponent: () => import('@partnercore/feature-auth-pages/auth/auth.component').then(m => m.AuthComponent),
    canActivate: [redirectAuthenticatedGuard],
    children: [
      {
        path: 'login',
        loadComponent: () => import('@partnercore/feature-auth-pages/auth-pages/login/login.component-page').then(m => m.LoginPageComponent),
      },
      {
        path: 'forgot-password',
        loadComponent: () => import('@partnercore/feature-auth-pages/auth-pages/forgot-password/forgot-password-page.component').then(m => m.ForgotPasswordPageComponent),
      },
      {
        path: 'reset-password',
        loadComponent: () => import('@partnercore/feature-auth-pages/auth-pages/reset-password/reset-password-page.component').then(m => m.ResetPasswordPageComponent),
      },
    ],
  },
  // Error
  {
    path: '404',
    loadComponent: () => import('@partnercore/feature-error-pages/other-page/not-found/not-found.component').then(m => m.NotFoundComponent),
    title: 'titles.notFound',
  },
  {
    path: '**',
    loadComponent: () => import('@partnercore/feature-error-pages/other-page/not-found/not-found.component').then(m => m.NotFoundComponent),
    title: 'titles.notFound',
  },
];
